import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, Users, HeartHandshake, Calendar, TrendingUp, Award, ArrowRight } from 'lucide-react';
import axios from 'axios';

const Home = () => {
  const [stats, setStats] = useState({
    volunteers: 150,
    helpRequests: 89,
    donations: 500000,
    events: 25
  });
  const [socialPosts, setSocialPosts] = useState([]);

  useEffect(() => {
    fetchSocialPosts();
  }, []);

  const fetchSocialPosts = async () => {
    try {
      const response = await axios.get(`${import.meta.env.VITE_API_URL}/social/posts?limit=6`);
      setSocialPosts(response.data.posts || []);
    } catch (error) {
      console.error('Error fetching social posts:', error);
    }
  };

  const features = [
    {
      icon: Users,
      title: 'Volunteer Programs',
      description: 'Join our community of dedicated volunteers making a difference',
      link: '/volunteer'
    },
    {
      icon: HeartHandshake,
      title: 'Request Help',
      description: 'We are here to support you in times of need',
      link: '/help'
    },
    {
      icon: Heart,
      title: 'Make a Donation',
      description: 'Your contribution helps us serve more communities',
      link: '/donate'
    },
    {
      icon: Calendar,
      title: 'Upcoming Events',
      description: 'Participate in our community events and programs',
      link: '/events'
    }
  ];

  const impactStats = [
    { icon: Users, label: 'Active Volunteers', value: stats.volunteers, suffix: '+' },
    { icon: HeartHandshake, label: 'Lives Impacted', value: stats.helpRequests, suffix: '+' },
    { icon: TrendingUp, label: 'Donations Raised', value: `₹${(stats.donations / 1000).toFixed(0)}K`, suffix: '+' },
    { icon: Award, label: 'Events Conducted', value: stats.events, suffix: '+' }
  ];

  return (
    <div className="bg-gray-50 font-sans">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-dark via-primary to-primary-light text-white overflow-hidden min-h-[600px] flex items-center">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
        <div className="absolute right-0 top-0 w-1/2 h-full bg-white/5 skew-x-12 transform translate-x-20"></div>

        <div className="relative container-custom section-padding">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-2xl"
            >
              <h1 className="text-5xl lg:text-7xl font-heading font-bold mb-6 leading-tight">
                Service to <br />
                <span className="text-accent underline decoration-4 underline-offset-4">Humanity</span>
              </h1>
              <p className="text-xl text-primary-light mb-8 leading-relaxed">
                Join us in making a difference. Together, we can create lasting change and bring hope to communities in need through dedicated service and support.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/volunteer" className="btn btn-accent text-white hover:scale-105 transition-transform">
                  Become a Volunteer
                </Link>
                <Link to="/help" className="btn bg-white/10 backdrop-blur-sm border border-white/30 text-white hover:bg-white/20">
                  Request Help
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="hidden md:block relative"
            >
              {/* Abstract Modern Shape instead of generic blobs */}
              <div className="relative z-10 grid grid-cols-2 gap-4">
                <div className="space-y-4 pt-12">
                  <div className="bg-white/10 backdrop-blur-md p-6 rounded-2xl border border-white/20 transform hover:-translate-y-2 transition-transform duration-500">
                    <Heart className="w-10 h-10 text-accent mb-4" />
                    <h3 className="text-xl font-bold mb-1">Compassion</h3>
                    <p className="text-sm opacity-80">Driven by love for humanity</p>
                  </div>
                  <div className="bg-primary-dark/80 backdrop-blur-md p-6 rounded-2xl border border-white/10 transform hover:-translate-y-2 transition-transform duration-500">
                    <Users className="w-10 h-10 text-primary-light mb-4" />
                    <h3 className="text-xl font-bold mb-1">Community</h3>
                    <p className="text-sm opacity-80">Building stronger bonds</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="bg-white text-primary-dark p-6 rounded-2xl shadow-xl transform hover:-translate-y-2 transition-transform duration-500">
                    <div className="text-4xl font-bold mb-1">100%</div>
                    <p className="font-medium">Transparent</p>
                  </div>
                  <div className="bg-accent p-6 rounded-2xl shadow-lg transform hover:-translate-y-2 transition-transform duration-500">
                    <HeartHandshake className="w-10 h-10 text-white mb-4" />
                    <h3 className="text-xl font-bold text-white mb-1">Support</h3>
                    <p className="text-sm text-white/90">Always here to help</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="py-12 bg-white relative z-20 -mt-10 container-custom">
        <div className="bg-white rounded-3xl shadow-soft p-8 md:p-12 border border-gray-100">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {impactStats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="text-center group"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-light/30 rounded-2xl mb-4 group-hover:scale-110 transition-transform duration-300">
                  <stat.icon className="w-8 h-8 text-primary-dark" />
                </div>
                <h3 className="text-4xl font-heading font-bold text-gray-900 mb-1">
                  {stat.value}{stat.suffix}
                </h3>
                <p className="text-gray-500 font-medium">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <span className="text-primary font-semibold tracking-wider uppercase text-sm mb-2 block">What We Do</span>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-gray-900 mb-6">How You Can Help</h2>
            <p className="text-xl text-gray-600">
              Multiple ways to make a positive impact in your community through our various initiatives.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -8 }}
              >
                <Link to={feature.link} className="card p-8 h-full flex flex-col items-start group hover:border-primary/30">
                  <div className="w-14 h-14 bg-gray-50 rounded-xl mb-6 flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-all duration-300">
                    <feature.icon className="w-7 h-7 text-gray-600 group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-primary transition-colors">{feature.title}</h3>
                  <p className="text-gray-600 mb-6 flex-grow">{feature.description}</p>
                  <div className="flex items-center text-primary font-semibold group-hover:tracking-wide transition-all">
                    Learn more <ArrowRight className="w-4 h-4 ml-2" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <span className="text-primary font-semibold tracking-wider uppercase text-sm mb-2 block">Our Impact in Pictures</span>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-gray-900 mb-6">Gallery</h2>
            <p className="text-xl text-gray-600">
              Capturing moments of hope, service, and community transformation across our various initiatives.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((item) => (
              <motion.div
                key={item}
                whileHover={{ scale: 1.02 }}
                className="relative aspect-square overflow-hidden rounded-2xl group cursor-pointer"
              >
                <img
                  src={`https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?q=80&w=600&auto=format&fit=crop&sig=${item}`}
                  alt={`Gallery ${item}`}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-primary/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <span className="text-white font-bold text-lg">View</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* YouTube Section */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <span className="text-primary font-semibold tracking-wider uppercase text-sm mb-2 block">Watch Our Work</span>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-gray-900 mb-6">YouTube Videos</h2>
            <p className="text-xl text-gray-600">
              See our missions in action through our official YouTube channel updates.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 justify-items-center">
            {['1JrZZK9EyfI', 'B7rdjTH_GiI', '1JrZZK9EyfI'].map((id, index) => (
              <div key={index} className="w-full max-w-[315px] aspect-[9/16] rounded-2xl overflow-hidden shadow-lg border border-gray-100 bg-black">
                <iframe
                  className="w-full h-full"
                  src={`https://www.youtube.com/embed/${id}`}
                  title="YouTube video player"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="flex justify-between items-end mb-16">
            <div>
              <span className="text-primary font-semibold tracking-wider uppercase text-sm mb-2 block">Latest Updates</span>
              <h2 className="text-4xl md:text-5xl font-heading font-bold text-gray-900">Blog</h2>
            </div>
            <Link to="/blog" className="hidden md:flex items-center text-primary font-semibold hover:text-primary-dark transition-colors">
              Read all posts <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                title: 'Healthcare for Rural Communities',
                date: 'January 15, 2026',
                excerpt: 'Providing essential medical services to those who have limited access...',
                image: 'https://images.unsplash.com/photo-1576091160550-2173bdb999ef?q=80&w=800&auto=format&fit=crop'
              },
              {
                title: 'Education for Every Child',
                date: 'January 10, 2026',
                excerpt: 'Our initiative to bring digital learning to rural primary schools...',
                image: 'https://images.unsplash.com/photo-1497633762265-9d179a990aa6?q=80&w=800&auto=format&fit=crop'
              },
              {
                title: 'Winter Relief Program 2026',
                date: 'January 5, 2026',
                excerpt: 'Distributing blankets and warm clothing to the needy in North India...',
                image: 'https://images.unsplash.com/photo-1469571483353-9133bd4751ff?q=80&w=800&auto=format&fit=crop'
              }
            ].map((post, index) => (
              <div key={index} className="card overflow-hidden group">
                <div className="h-56 overflow-hidden">
                  <img src={post.image} alt={post.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="p-8">
                  <span className="text-primary text-sm font-bold mb-2 block">{post.date}</span>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-primary transition-colors">{post.title}</h3>
                  <p className="text-gray-600 mb-6">{post.excerpt}</p>
                  <Link to={`/blog/${index}`} className="flex items-center text-primary font-semibold">
                    Read more <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section-padding bg-gray-50 overflow-hidden">
        <div className="container-custom">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <span className="text-primary font-semibold tracking-wider uppercase text-sm mb-2 block">Voices of Impact</span>
            <h2 className="text-4xl md:text-5xl font-heading font-bold text-gray-900 mb-6">Testimonials</h2>
            <p className="text-xl text-gray-600">
              Heartwarming stories from the people whose lives have been touched by your generosity.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                name: 'Rajesh Kumar',
                role: 'Beneficiary',
                text: 'The medical camp organized by Sevasaredham saved my father\'s life. We are eternally grateful for their support.'
              },
              {
                name: 'Priya Sharma',
                role: 'Volunteer',
                text: 'Volunteering with this team has been a life-changing experience. Seeing the smiles on children\'s faces is the best reward.'
              },
              {
                name: 'Amit Singh',
                role: 'Donor',
                text: 'I trust Sevasaredham because of their absolute transparency. I know my contributions are directly helping those in need.'
              }
            ].map((testimonial, index) => (
              <div key={index} className="card p-8 bg-white relative">
                <div className="absolute -top-4 left-8 text-6xl text-primary/20 font-serif">"</div>
                <p className="text-gray-700 italic mb-8 relative z-10">{testimonial.text}</p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold mr-4">
                    {testimonial.name[0]}
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900">{testimonial.name}</h4>
                    <p className="text-sm text-gray-500">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-primary-dark text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-full bg-white/5 skew-x-12 transform translate-x-20"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-accent/20 rounded-full blur-3xl"></div>

        <div className="container-custom text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-4xl md:text-5xl font-heading font-bold mb-6">Make a Difference Today</h2>
            <p className="text-xl text-primary-light mb-10 leading-relaxed">
              Your contribution, no matter how small, can transform lives and bring hope to communities in need. Every second counts.
            </p>
            <Link to="/donate" className="btn btn-accent text-lg px-10 py-4 shadow-xl hover:shadow-2xl transform hover:-translate-y-1">
              Donate Now
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
